import React, { useCallback, useEffect, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../firebase';

const ADMIN_API_BASE = 'https://us-central1-northpet.cloudfunctions.net';

const initialProductForm = {
  docId: '',
  id: '',
  name: '',
  description: '',
  price: '',
  image: '',
  amazonLink: '',
  walmartLink: '',
  category: '',
  stock: '',
};

const formatOrderCreatedAt = (createdAt) => {
  if (!createdAt) {
    return 'Pending';
  }

  if (typeof createdAt === 'string' || typeof createdAt === 'number') {
    const parsed = new Date(createdAt);
    return Number.isNaN(parsed.getTime()) ? 'Pending' : parsed.toLocaleString();
  }

  if (createdAt instanceof Date) {
    return Number.isNaN(createdAt.getTime()) ? 'Pending' : createdAt.toLocaleString();
  }

  if (typeof createdAt.toDate === 'function') {
    const parsed = createdAt.toDate();
    return parsed instanceof Date && !Number.isNaN(parsed.getTime()) ? parsed.toLocaleString() : 'Pending';
  }

  const seconds = createdAt.seconds ?? createdAt._seconds;
  const nanoseconds = createdAt.nanoseconds ?? createdAt._nanoseconds ?? 0;

  if (typeof seconds === 'number') {
    const parsed = new Date((seconds * 1000) + Math.floor(nanoseconds / 1000000));
    return Number.isNaN(parsed.getTime()) ? 'Pending' : parsed.toLocaleString();
  }

  return 'Pending';
};

const Admin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [productForm, setProductForm] = useState(initialProductForm);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [savingProduct, setSavingProduct] = useState(false);
  const [updatingOrderIds, setUpdatingOrderIds] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        const tokenResult = await currentUser.getIdTokenResult();
        setIsAdmin(!!tokenResult.claims.admin);
      } else {
        setIsAdmin(false);
      }
      setAuthLoading(false);
    });
    return unsubscribe;
  }, []);

  const isAuthenticated = !!user;
  const isAuthorized = isAdmin || (user && user.email === 'fikrat86@gmail.com');

  const getHeaders = useCallback(async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('No authenticated user');
    }
    
    let token = await currentUser.getIdToken();
    const tokenResult = await currentUser.getIdTokenResult();
    
    // If the user email matches the primary admin, but they don't have the claim yet,
    // they might have just been promoted. Force-refresh token to get the new claim.
    if (!tokenResult.claims.admin && currentUser.email === 'fikrat86@gmail.com') {
      token = await currentUser.getIdToken(true);
      const updatedResult = await currentUser.getIdTokenResult();
      if (updatedResult.claims.admin) {
        setIsAdmin(true);
      }
    }
    
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !isAuthorized) {
      return undefined;
    }

    const q = query(collection(db, 'products'), orderBy('id', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const productItems = [];
      snapshot.forEach((doc) => {
        productItems.push({ docId: doc.id, ...doc.data() });
      });
      setProducts(productItems);
    }, (snapshotError) => {
      setError(snapshotError.message || 'Failed to load products');
    });

    return unsubscribe;
  }, [isAuthenticated, isAuthorized]);

  const loadOrders = useCallback(async () => {
    setLoadingOrders(true);
    setError('');

    try {
      const headers = await getHeaders();
      const response = await fetch(`${ADMIN_API_BASE}/adminListOrders?limit=25`, {
        headers,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to load orders');
      }

      setOrders((data.orders || []).map((order) => ({
        ...order,
        createdAt: order.createdAt || null,
      })));

      // If user is primary admin but claims.admin is not set in React state yet,
      // force refresh to pick up the promotion claim after first success.
      if (auth.currentUser && auth.currentUser.email === 'fikrat86@gmail.com' && !isAdmin) {
        const tokenResult = await auth.currentUser.getIdTokenResult(true);
        if (tokenResult.claims.admin) {
          setIsAdmin(true);
        }
      }
    } catch (loadError) {
      setError(loadError.message || 'Failed to load orders');
    } finally {
      setLoadingOrders(false);
    }
  }, [getHeaders, isAdmin]);

  useEffect(() => {
    if (isAuthenticated && isAuthorized) {
      loadOrders();
    }
  }, [isAuthenticated, isAuthorized, loadOrders]);

  const handleLogin = async (event) => {
    event.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError('Enter your admin email and password.');
      return;
    }

    setError('');
    setSuccess('');
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password.trim());
      setSuccess('Admin access granted.');
    } catch (loginError) {
      setError(loginError.message || 'Failed to authenticate.');
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setOrders([]);
      setProducts([]);
      setProductForm(initialProductForm);
      setSuccess('Logged out successfully.');
      setError('');
    } catch (logoutError) {
      setError(logoutError.message || 'Failed to log out.');
    }
  };

  const handleProductChange = (event) => {
    const { name, value } = event.target;
    setProductForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const resetProductForm = () => {
    setProductForm(initialProductForm);
    setSuccess('');
    setError('');
  };

  const fillProductForm = (product) => {
    setActiveTab('products');
    setProductForm({
      docId: product.docId || '',
      id: String(product.id || ''),
      name: product.name || '',
      description: product.description || '',
      price: String(product.price || ''),
      image: product.image || '',
      amazonLink: product.amazonLink || '',
      walmartLink: product.walmartLink || '',
      category: product.category || '',
      stock: product.stock !== null && product.stock !== undefined ? String(product.stock) : '',
    });
  };

  const saveProduct = async (event) => {
    event.preventDefault();
    setSavingProduct(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        id: productForm.id,
        name: productForm.name,
        description: productForm.description,
        price: productForm.price,
        image: productForm.image,
        amazonLink: productForm.amazonLink,
        walmartLink: productForm.walmartLink,
        category: productForm.category,
        stock: productForm.stock,
      };

      const endpoint = productForm.docId
        ? `${ADMIN_API_BASE}/adminUpdateProduct`
        : `${ADMIN_API_BASE}/adminCreateProduct`;

      if (productForm.docId) {
        payload.productDocId = productForm.docId;
      }

      const headers = await getHeaders();
      const response = await fetch(endpoint, {
        method: productForm.docId ? 'PUT' : 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to save product');
      }

      setSuccess(productForm.docId ? 'Product updated.' : 'Product added.');
      resetProductForm();
      loadOrders();
    } catch (saveError) {
      setError(saveError.message || 'Failed to save product');
    } finally {
      setSavingProduct(false);
    }
  };

  const deleteProduct = async (docId) => {
    if (!window.confirm('Delete this product?')) {
      return;
    }

    try {
      const headers = await getHeaders();
      const response = await fetch(`${ADMIN_API_BASE}/adminDeleteProduct/${docId}`, {
        method: 'DELETE',
        headers,
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete product');
      }

      setSuccess('Product deleted.');
      if (productForm.docId === docId) {
        resetProductForm();
      }
    } catch (deleteError) {
      setError(deleteError.message || 'Failed to delete product');
    }
  };

  const statusBadgeClass = (status) => {
    if (status === 'completed') return 'bg-green-100 text-green-800';
    if (status === 'payment_failed') return 'bg-red-100 text-red-800';
    return 'bg-yellow-100 text-yellow-800';
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl font-semibold text-gray-600">Loading Auth...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <div className="max-w-xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
            <h1 className="text-3xl font-bold text-forest-green mb-3">Admin Access</h1>
            <p className="text-gray-600 mb-6">
              Enter your administrator email and password to access dashboard.
            </p>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="admin@northpet.ca"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-forest-green"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="••••••••"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-forest-green"
                  required
                />
              </div>
              {error && <div className="p-3 rounded-lg bg-red-100 text-red-700 text-sm">{error}</div>}
              <button
                type="submit"
                className="w-full bg-forest-green text-white py-3 rounded-lg font-semibold hover:bg-opacity-90 transition"
              >
                Sign In
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <div className="max-w-xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200 text-center">
            <h1 className="text-3xl font-bold text-red-600 mb-3">Access Denied</h1>
            <p className="text-gray-600 mb-6">
              You are signed in as <span className="font-semibold">{user.email}</span>, which is not authorized to access the Admin Dashboard.
            </p>
            {error && <div className="mb-4 p-3 rounded-lg bg-red-100 text-red-700 text-sm">{error}</div>}
            <button
              onClick={handleLogout}
              className="w-full bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-opacity-90 transition"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-forest-green">Admin Dashboard</h1>
            <p className="text-gray-600 mt-2">Manage products, view orders, and handle store operations.</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={loadOrders}
              className="px-4 py-2 rounded-lg bg-moss-green text-white font-semibold hover:bg-opacity-90"
            >
              Refresh Orders
            </button>
            <button
              onClick={handleLogout}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-white"
            >
              Log Out
            </button>
          </div>
        </div>

        {error && <div className="mb-6 p-4 rounded-lg bg-red-100 text-red-700">{error}</div>}
        {success && <div className="mb-6 p-4 rounded-lg bg-green-100 text-green-700">{success}</div>}

        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden mb-8">
          <div className="flex flex-wrap border-b border-gray-200">
            {[
              { key: 'orders', label: 'Orders' },
              { key: 'products', label: 'Products' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-6 py-4 font-semibold ${activeTab === tab.key ? 'text-forest-green border-b-2 border-forest-green bg-green-50' : 'text-gray-600 hover:text-forest-green'}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'orders' && (
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-800">Recent Orders</h2>
                {loadingOrders && <span className="text-sm text-gray-500">Loading...</span>}
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="py-3 pr-4">Order</th>
                      <th className="py-3 pr-4">Customer</th>
                      <th className="py-3 pr-4">Amount</th>
                      <th className="py-3 pr-4">Status</th>
                      <th className="py-3 pr-4">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="py-8 text-gray-500">No orders loaded yet.</td>
                      </tr>
                    ) : (
                      orders.map((order) => (
                        <tr key={order.id} className="border-b last:border-b-0">
                          <td className="py-3 pr-4 font-mono text-xs">{order.id}</td>
                          <td className="py-3 pr-4">{order.customerName || order.customerEmail || 'Unknown'}</td>
                          <td className="py-3 pr-4">${Number(order.totalAmount || 0).toFixed(2)} {order.currency || 'CAD'}</td>
                          <td className="py-3 pr-4">
                            <div className="flex items-center gap-2">
                              <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${statusBadgeClass(order.status)}`}>
                                {order.status || 'pending'}
                              </span>
                              <select
                                defaultValue={order.status || 'pending'}
                                onChange={async (e) => {
                                  const newStatus = e.target.value;
                                  setError('');
                                  setSuccess('');
                                  setUpdatingOrderIds((s) => ({ ...s, [order.id]: true }));
                                  try {
                                    const headers = await getHeaders();
                                    const resp = await fetch(`${ADMIN_API_BASE}/adminUpdateOrderStatus`, {
                                      method: 'POST',
                                      headers,
                                      body: JSON.stringify({ orderId: order.id, status: newStatus }),
                                    });
                                    const payload = await resp.json();
                                    if (!resp.ok) throw new Error(payload.error || 'Failed to update status');
                                    setSuccess('Order status updated');
                                    await loadOrders();
                                  } catch (err) {
                                    setError(err.message || 'Failed to update status');
                                  } finally {
                                    setUpdatingOrderIds((s) => ({ ...s, [order.id]: false }));
                                  }
                                }}
                                className="px-2 py-1 border rounded text-sm"
                                disabled={!!updatingOrderIds[order.id]}
                              >
                                <option value="pending">pending</option>
                                <option value="paid">paid</option>
                                <option value="prepared_for_shipping">prepared for shipping</option>
                                <option value="ready_for_pickup">ready for pickup</option>
                                <option value="shipped">shipped</option>
                                <option value="delivered">delivered</option>
                                <option value="payment_failed">payment_failed</option>
                                <option value="cancelled">cancelled</option>
                                <option value="completed">completed</option>
                              </select>
                            </div>
                          </td>
                          <td className="py-3 pr-4 text-gray-600">
                            {formatOrderCreatedAt(order.createdAt)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'products' && (
            <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {productForm.docId ? 'Edit Product' : 'Add Product'}
                </h2>

                <form onSubmit={saveProduct} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="text"
                      name="id"
                      value={productForm.id}
                      onChange={handleProductChange}
                      placeholder="Product ID"
                      className="px-4 py-3 border border-gray-300 rounded-lg"
                      required
                    />
                    <input
                      type="number"
                      name="price"
                      value={productForm.price}
                      onChange={handleProductChange}
                      placeholder="Price"
                      step="0.01"
                      className="px-4 py-3 border border-gray-300 rounded-lg"
                      required
                    />
                  </div>

                  <input
                    type="text"
                    name="name"
                    value={productForm.name}
                    onChange={handleProductChange}
                    placeholder="Product name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                    required
                  />

                  <textarea
                    name="description"
                    value={productForm.description}
                    onChange={handleProductChange}
                    placeholder="Product description"
                    rows="4"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                    required
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="url"
                      name="amazonLink"
                      value={productForm.amazonLink}
                      onChange={handleProductChange}
                      placeholder="Amazon link"
                      className="px-4 py-3 border border-gray-300 rounded-lg"
                      required
                    />
                    <input
                      type="url"
                      name="walmartLink"
                      value={productForm.walmartLink}
                      onChange={handleProductChange}
                      placeholder="Walmart link"
                      className="px-4 py-3 border border-gray-300 rounded-lg"
                      required
                    />
                  </div>

                  <input
                    type="url"
                    name="image"
                    value={productForm.image}
                    onChange={handleProductChange}
                    placeholder="Image URL"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="text"
                      name="category"
                      value={productForm.category}
                      onChange={handleProductChange}
                      placeholder="Category"
                      className="px-4 py-3 border border-gray-300 rounded-lg"
                    />
                    <input
                      type="number"
                      name="stock"
                      value={productForm.stock}
                      onChange={handleProductChange}
                      placeholder="Stock"
                      className="px-4 py-3 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={savingProduct}
                      className="px-5 py-3 rounded-lg bg-forest-green text-white font-semibold hover:bg-opacity-90 disabled:opacity-50"
                    >
                      {savingProduct ? 'Saving...' : productForm.docId ? 'Update Product' : 'Add Product'}
                    </button>
                    <button
                      type="button"
                      onClick={resetProductForm}
                      className="px-5 py-3 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50"
                    >
                      Reset
                    </button>
                  </div>
                </form>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Current Products</h2>
                <div className="space-y-4 max-h-[38rem] overflow-y-auto pr-1">
                  {products.length === 0 ? (
                    <div className="p-4 rounded-lg bg-gray-50 text-gray-500">No products loaded.</div>
                  ) : (
                    products.map((product) => (
                      <div key={product.docId} className="p-4 border border-gray-200 rounded-xl bg-gray-50">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="font-semibold text-gray-800">{product.name}</h3>
                            <p className="text-sm text-gray-600 line-clamp-2">{product.description}</p>
                            <p className="text-sm text-forest-green font-semibold mt-2">
                              ${Number(product.price || 0).toFixed(2)} CAD
                            </p>
                          </div>
                          <div className="flex gap-2 shrink-0">
                            <button
                              onClick={() => fillProductForm(product)}
                              className="px-3 py-2 rounded-lg bg-moss-green text-white text-sm font-semibold"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => deleteProduct(product.docId)}
                              className="px-3 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl shadow p-5 border border-gray-200">
            <p className="text-sm text-gray-500">Orders Loaded</p>
            <p className="text-3xl font-bold text-forest-green mt-2">{orders.length}</p>
          </div>
          <div className="bg-white rounded-xl shadow p-5 border border-gray-200">
            <p className="text-sm text-gray-500">Products</p>
            <p className="text-3xl font-bold text-forest-green mt-2">{products.length}</p>
          </div>
          <div className="bg-white rounded-xl shadow p-5 border border-gray-200">
            <p className="text-sm text-gray-500">Admin Mode</p>
            <p className="text-base font-semibold text-gray-800 mt-2 truncate" title={user?.email || ''}>
              Active: {user?.email || ''}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;
